import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {Button, CircularProgress, Grid, Typography, useTheme, withStyles} from "@material-ui/core";
import classnames from 'classnames';
import {FormattedMessage} from "react-intl";
import formatMoney from "../../../utils/formatMoney";
import CreditCardInput from 'react-credit-card-input';
import CheckoutAPI from "../../../api/CheckoutAPI";

const styles = (theme) => ({
  container: {
    marginTop: theme.spacing(0.5)
  },
  formControl: {
    padding: '2px 4px',
    display: 'flex',
    alignItems: 'flex-start',
    flexDirection: 'row',
    height: 40,
  },
  inputContainer: {
    marginTop: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius+ 'px !important',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderWidth: '1px',
    borderStyle: 'solid',
    paddingLeft: theme.spacing(1) + 'px !important',
    paddingRight: theme.spacing(1)+ 'px !important',
    paddingTop: theme.spacing(2)+ 'px !important',
    paddingBottom: theme.spacing(2)+ 'px !important',
  },
  label: {
    paddingBottom: theme.spacing(1)
  },
  skeletonCardField: {
    width: '100%',
    height: 50,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(0.5),
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '16px',
    paddingBottom: '16px',
  },
  hidden: {
    display: 'none'
  },
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  error: {
    color: theme.palette.error.main
  },
  cloverFooter: {
    padding: '18px 24px',
    borderRadius: '0 0 14px 14px',
    textAlign: 'center'
  },
  cloverLockImage: {
    top: 2,
    position: 'relative',
    marginRight: theme.spacing(1)
  },
  cloverLogo: {
    maxWidth: 20,
    position: 'relative',
    top: 4,
    marginLeft: theme.spacing(1)
  },
  cloverPowered: {
    color: theme.palette.text.disabled,
    fontWeight: 'bold'
  },
  cloverPrivacyPolicy: {
    color: theme.palette.text.disabled,
  }
});

CloverDevPayCardDetailsForm.propTypes = {
  cloverMerchantId: PropTypes.string.isRequired,
  exponent: PropTypes.string.isRequired,
  modulus: PropTypes.string.isRequired,
  prefix: PropTypes.string.isRequired,
  initializingPaymentConfig: PropTypes.bool.isRequired,
  showZip: PropTypes.bool,
  amountDue: PropTypes.object.isRequired,
  idempotencyKey: PropTypes.string.isRequired,
  onPaymentTokenCreated: PropTypes.func.isRequired,
  classes: PropTypes.object
};

function CloverDevPayCardDetailsForm({
                                  cloverMerchantId,
                                   exponent,
                                   modulus,
                                   prefix,
                                  initializingPaymentConfig,
                                  showZip = true,
                                  amountDue,
                                  idempotencyKey,
                                  onPaymentTokenCreated,
                                  classes,
                                }) {

  const [initialized, setInitialized] = useState(false);

  const [cardNumber, setCardNumber] = useState(null);
  const [cardNumberError, setCardNumberError] = useState(null);
  const [cardNumberValid, setCardNumberValid] = useState(false);

  const [cardExpiry, setCardExpiry] = useState(null);
  const [cardExpiryError, setCardExpiryError] = useState(null);
  const [cardExpiryValid, setCardExpiryValid] = useState(false);

  const [cardCvc, setCardCvc] = useState(null);
  const [cardCvcError, setCardCvcError] = useState(null);
  const [cardCvcValid, setCardCvcValid] = useState(false);

  const [cardZip, setCardZip] = useState(null);
  const [cardZipError, setCardZipError] = useState(null);
  const [cardZipValid, setCardZipValid] = useState(false);

  const [paying, setPaying] = useState(false);
  const [idempotencyPaymentTokenMap, setIdempotencyPaymentTokenMap] = useState(new Map());

  const theme = useTheme();

  function handleOnPayClick() {
    if (validate()) {
      setPaying(true);
      // Check if a payment token already was created for the given idempotency key
      // If yes, return the created payment token
      if (idempotencyPaymentTokenMap.get(idempotencyKey)) {
        onPaymentTokenCreated(idempotencyPaymentTokenMap.get(idempotencyKey))
          .then(() => {
            setPaying(false);
          })
          .catch((errorMessage) => {
            setPaying(false);
          });
      } else {
        const paymentCardNumber = cardNumber.replace(/\s/g, '');
        const expirySegments = cardExpiry.split('/');
        const expMonth = parseInt(expirySegments[0].trim(), 10);
        const expYear = parseInt('20' + expirySegments[1].trim(), 10);

        const first6 = paymentCardNumber.substring(0, 6);
        const last4 = paymentCardNumber.substr(paymentCardNumber.length - 4);

        CheckoutAPI.encryptCardNumber(paymentCardNumber, prefix, exponent, modulus)
          .then((cardEncrypted) => {
            // Create a Clover token
            const paymentToken = {
              paymentProvider: 'CLOVER',
              type: 'CARD',
              cloverMerchantId,
              cardEncrypted,
              expMonth,
              expYear,
              cvv: cardCvc,
              zip: cardZip,
              first6,
              last4
            };

            idempotencyPaymentTokenMap.set(idempotencyKey, paymentToken);
            setIdempotencyPaymentTokenMap(idempotencyPaymentTokenMap);
            onPaymentTokenCreated(paymentToken)
              .then(() => {
                setPaying(false);
              })
              .catch((errorMessage) => {
                setPaying(false);
              });
          })
          .catch((errorResponse) => {
            setPaying(false);
          });
      }

    }
  }

  function validate() {
    let valid = true;
    if (!cardNumberValid) {
      valid = false;
      if (cardNumberError === null) {
        setCardNumberError('Card number is required.');
      }

    }
    if (!cardExpiryValid) {
      valid = false;
      if (cardExpiryError === null) {
        setCardExpiryError('Expiration is required.');
      }
    }
    if (!cardCvcValid) {
      valid = false;
      if (cardCvcError === null) {
        setCardCvcError('CVV is required.');
      }
    }
    if (!cardZipValid) {
      valid = false;
      if (cardZipError === null) {
        setCardZipError('Postal code is required.');
      }
    }
    return valid;
  }

  const cardFieldClass = initialized ? classes.cardField : classnames(classes.cardField, classes.hidden);

  return (
    <Grid container spacing={2} className={classes.container}>
      <Grid item xs={12}>
        <Typography variant={'button'} className={classes.label}>
          <FormattedMessage id={'label.card-details'}/>
        </Typography>
        <CreditCardInput
          cardNumberInputProps={{
            value: cardNumber,
            onChange: (e) => {
              setCardNumber(e.target.value);
              setCardNumberValid(true);
            },
            onError: (error) => {
              setCardNumberError(error.message);
              setCardNumberValid(false);
            }
          }}
          cardExpiryInputProps={{
            value: cardExpiry,
            onChange: (e) => {
              setCardExpiry(e.target.value);
              setCardExpiryValid(true);
            },
            onError: (error) => {
              setCardExpiryError(error.message);
              setCardExpiryValid(false);
            }
          }}
          cardCVCInputProps={{
            value: cardCvc,
            onChange: (e) => {
              setCardCvc(e.target.value);
              setCardCvcValid(true);
            },
            onError: (error) => {
              setCardCvcError(error.message);
              setCardCvcValid(false);
            }
          }}
          cardZipInputProps={{
            value: cardZip,
            onChange: (e) => {
              setCardZip(e.target.value);
              setCardZipValid(true);
            },
            onError: (error) => {
              setCardZipError(error.message);
              setCardZipValid(false);
            }
          }}
          enableZipInput={showZip}
          fieldClassName={classes.inputContainer}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          variant={'contained'}
          color={'primary'}
          fullWidth={true}
          size={'large'}
          data-test={'button-pay'}
          disabled={paying}
          onClick={handleOnPayClick}
        >
          {paying && <CircularProgress size={24} thickness={2}/>}
          {!paying && <FormattedMessage id={"button.pay"}
                                        values={{amount: formatMoney(amountDue.amount, amountDue.currencyCode)}}/>}
        </Button>
      </Grid>

      <Grid item xs={12} className={classes.center}>
        <Typography variant={'body2'} className={classes.cloverPowered}>
          <img className={classes.cloverLockImage}
               alt={'clover lock icon'}
               src={'https://checkout.sandbox.dev.clover.com/assets/icons/lock.png'}/>
          Secure Payments Powered by Clover
          <img
            className={classes.cloverLogo}
            alt={'clover logo'}
            src={'https://checkout.sandbox.dev.clover.com/assets/icons/clover-symbol.png'}/>
        </Typography>
        <Typography variant={'body2'}>
          <a  className={classes.cloverPrivacyPolicy} title="Privacy Policy" href={'https://www.clover.com/privacy-policy'}>Privacy Policy</a>
        </Typography>
      </Grid>
    </Grid>
  );
}

export default withStyles(styles)(CloverDevPayCardDetailsForm);
